/**
 * KraftStories — Scroll Depth
 * ---------------------------------------------------------------------------
 * GA4's own "scroll" event is unusable here: it fires from the Shopify
 * Customer-Events pixel sandbox, whose document is a few pixels tall, so the
 * 90% threshold is true the moment any page loads. 28,205 "scrolls" in the
 * Jul–Aug window were exactly that — one per pageview, measuring nothing.
 * (See SCROLL_DEPTH_TRACKING.md for the full story and the pixel fix.)
 *
 * This file measures the real main-frame document and emits four events:
 *
 *   scroll_depth_25   scroll_depth_50   scroll_depth_75   scroll_depth_90
 *
 * The threshold lives in the EVENT NAME, not only a parameter, for the same
 * reason ab-engine.js bakes the variant into its event names: Windsor.ai (the
 * reporting pipeline) can only segment by registered custom dimensions, and
 * event names need no registration. percent_scrolled is still attached as a
 * parameter for GA4-native reports; registering it as a custom dimension is
 * optional, not required.
 *
 * Rules:
 *   - each threshold fires at most once per pageview
 *   - depth = (viewport bottom) / document height, so 100% is reachable
 *   - pages shorter than ~1.05 viewports emit NOTHING: an unscrollable page
 *     tells you about the layout, not the visitor. GA4's enhanced measurement
 *     gets this wrong on purpose-built landing pages; we do not.
 *   - rAF-throttled passive listener; detaches itself after 90% fires
 *   - routes through window.__kraftGa4Mirror (analytics-events.js) so the GTM
 *     double-count rule stays in exactly one file, with the same gtag
 *     fallback header-analytics.js and section-analytics.js use
 */
(function () {
  'use strict';

  var THRESHOLDS = [25, 50, 75, 90];
  var MIN_SCROLLABLE_RATIO = 1.05;

  var fired = {};
  var ticking = false;
  var done = false;

  function emit(threshold) {
    var name = 'scroll_depth_' + threshold;
    var params = { percent_scrolled: threshold };
    if (typeof window.__kraftGa4Mirror === 'function') {
      window.__kraftGa4Mirror(name, params);
    } else if (typeof window.gtag === 'function') {
      /* analytics-events.js missing or still loading: gtag direct is better
         than losing the event, and a duplicate is impossible because the
         helper is the only other path. */
      window.gtag('event', name, params);
    }
  }

  function docHeight() {
    var d = document.documentElement, b = document.body;
    return Math.max(
      d ? d.scrollHeight : 0, b ? b.scrollHeight : 0,
      d ? d.offsetHeight : 0, b ? b.offsetHeight : 0
    );
  }

  function measure() {
    ticking = false;
    if (done) return;

    var height = docHeight();
    var viewport = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
    if (!height || !viewport) return;

    /* Unscrollable (or nearly so): say nothing rather than claim depth. */
    if (height <= viewport * MIN_SCROLLABLE_RATIO) return;

    var bottom = (window.pageYOffset || (document.documentElement && document.documentElement.scrollTop) || 0) + viewport;
    var pct = (bottom / height) * 100;

    for (var i = 0; i < THRESHOLDS.length; i++) {
      var t = THRESHOLDS[i];
      if (pct >= t && !fired[t]) {
        fired[t] = true;
        emit(t);
      }
    }

    if (fired[90]) {
      done = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }
  }

  function onScroll() {
    if (ticking || done) return;
    ticking = true;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(measure);
    } else {
      setTimeout(measure, 66);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* A first measure on load: a visitor who lands mid-page via an anchor link
     has already "reached" that depth without a scroll event ever firing. */
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    onScroll();
  } else {
    document.addEventListener('DOMContentLoaded', onScroll);
  }
})();
